import { createRacquetEventQueue, racquetQueueKey } from './racquetEventQueue'
import { sendRacquetEvent } from './racquetEventRequest'
import { racquetPath, racquetResponse } from './racquetApi'
import { reconstructRacquet, reduceRacquet } from '../../../shared/racquetScoreDomain.mjs'
import { emitMemberClientTelemetry } from './diagnosticTelemetry'

const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
  ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value
const equalState = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
const deviceStorage = {
  getItem: key => globalThis.localStorage.getItem(key),
  setItem: (key, value) => globalThis.localStorage.setItem(key, value),
  removeItem: key => globalThis.localStorage.removeItem(key),
  key: index => globalThis.localStorage.key(index),
  get length() { return globalThis.localStorage.length }
}

export function projectRacquetMatch(server, pending) {
  const ids = new Set(server.events.map(e => e.id))
  const remaining = pending.filter(e => !ids.has(e.id))
  if (remaining.some((e, i) => e.expectedRevision !== server.revision + i)) throw new Error('revision_conflict')
  const state = remaining.some(e => ['undo', 'replace_last_rally'].includes(e.operation.type))
    ? reconstructRacquet(server.initialState, [...server.events, ...remaining])
    : remaining.reduce((state, e) => reduceRacquet(state, e.operation), server.state)
  return { ...server, state,
    revision: server.revision + remaining.length, events: [...server.events, ...remaining] }
}

// One store per authenticated app lifetime. Routes only subscribe; they never own
// network cancellation. Persistent queue/cache keys remain compatible with v1.
export function createRacquetSyncStore({ clubCode, subject, get, post, storage = deviceStorage }) {
  const enqueuedAt = new Map(), reads = new Map()
  const sessions = new Map(), active = new Map(), listeners = new Set(), controller = new AbortController()
  let disposed = false
  const prefix = `privana:racquet-match:v1:${encodeURIComponent(clubCode)}:${encodeURIComponent(subject)}:`
  const emit = () => { if (!disposed) listeners.forEach(fn => fn()) }
  // Fence reads by request order, baseline identity and queue mutations. A GET
  // may outlive an entire edit/ack cycle, leaving an empty queue again.
  const beginRead = id => {
    const session = sessions.get(id), generation = (reads.get(id) || 0) + 1
    reads.set(id, generation)
    return { id, generation, session, server: session?.server, version: session?.version }
  }
  const currentRead = ticket => !disposed && reads.get(ticket.id) === ticket.generation &&
    sessions.get(ticket.id) === ticket.session && (!ticket.session ||
      (ticket.session.server === ticket.server && ticket.session.version === ticket.version))
  const usableRead = (ticket, server) => currentRead(ticket) && server.id === ticket.id &&
    Number.isInteger(server.revision) && server.revision >= (ticket.session?.server.revision ?? 0)
  const read = id => get(racquetPath(clubCode, `/matches/${id}`), { signal: controller.signal }).then(racquetResponse)
  const cache = session => {
    if (disposed) return
    try { storage.setItem(prefix + session.id, JSON.stringify(session.server)); session.cacheError = false }
    catch { session.cacheError = true }
  }
  const refresh = session => {
    if (disposed) return
    const raw = session.queue.snapshot()
    const q = { ...raw, storageError: raw.storageError || (session.cacheError ? 'queue_storage_failed' : null), saved: raw.saved && !session.cacheError }
    try {
      // A failed local write retains the previous editable projection and all
      // in-memory work. Only retrying storage may advance the result screen.
      if (!q.storageError) {
        session.model = session.candidate || projectRacquetMatch(session.server, q.events)
        session.candidate = null
      }
    } catch { session.error = true; session.model = session.server }
    session.snapshot = q
    emit()
  }
  const install = (id, server) => {
    let session = sessions.get(id)
    if (session) return session
    session = { id, server, model: server, saving: false, error: false, version: 0 }
    sessions.set(id, session)
    session.queue = createRacquetEventQueue({ clubCode, memberId: server.memberId, matchId: id }, { storage, onChange: () => { session.version++; refresh(session) } })
    cache(session); refresh(session)
    return session
  }
  const accept = (session, response, batch) => {
    if (disposed) throw new Error('store_disposed')
    const started = performance.now()
    if (response.id !== session.id || !Number.isInteger(response.revision)) throw new Error('invalid_ack')
    const byId = new Map(session.server.events.map(e => [e.id, e]))
    for (const event of response.events) byId.set(event.id, event)
    const events = [...byId.values()].sort((a, b) => a.revision - b.revision)
    // A compact ack must extend our exact history. A concurrent unknown suffix
    // remains queued for explicit review rather than being silently rebased.
    if (events.length !== response.revision || events.some((e, i) => e.revision !== i + 1)) throw new Error('invalid_ack_history')
    const state = reconstructRacquet(session.server.initialState, events)
    if (!equalState(state, response.state)) throw new Error('invalid_ack_state')
    session.server = { ...session.server, state, revision: response.revision, events,
      completedAt: Object.hasOwn(response, 'completedAt') ? response.completedAt : session.server.completedAt }
    batch.forEach(e => enqueuedAt.delete(e.id))
    cache(session)
    emitMemberClientTelemetry({ event: 'racquet_ack_applied', requestId: response.requestId, ackMs: performance.now() - started, eventCount: batch.length })
  }
  const syncSession = session => {
    if (disposed || session.running || !session.queue.snapshot().events.length || session.queue.snapshot().blocked) return session.running
    if (session.queue.snapshot().storageError || session.cacheError) return
    session.saving = true; emit()
    session.running = session.queue.flush(async events => {
      const started = performance.now(), requestId = events.at(-1).id
      const queueMs = enqueuedAt.has(events[0].id) ? started - enqueuedAt.get(events[0].id) : null
      emitMemberClientTelemetry({ event: 'racquet_batch_start', requestId, queueMs, eventCount: events.length })
      const response = await sendRacquetEvent(post, racquetPath(clubCode, `/matches/${session.id}/events/batch`), { events },
        { signal: controller.signal, decode: racquetResponse, requestId })
      emitMemberClientTelemetry({ event: 'racquet_batch_ack', requestId, queueMs, totalMs: performance.now() - started, transactionMs: response.transactionMs, eventCount: events.length })
      return { ...response, requestId }
    }, { batchSize: 32, onAccepted: (response, batch) => accept(session, response, batch) })
      .finally(async () => {
        if (disposed) return
        session.running = null; session.saving = false
        if (session.queue.snapshot().blocked) {
          const ticket = beginRead(session.id)
          try {
            const server = await read(session.id)
            if (usableRead(ticket, server)) { session.server = server; cache(session) }
          } catch { /* Local work stays exportable even if recovery cannot load. */ }
        }
        refresh(session)
        if (session.syncRequested && !disposed) {
          session.syncRequested = false
          return syncSession(session)
        }
      })
    return session.running
  }
  const sync = () => Promise.all([...sessions.values()].map(session => {
    if (session.running) { session.syncRequested = true; return session.running }
    return syncSession(session)
  }))
  return {
    subscribe(fn, id) {
      listeners.add(fn); if (id) active.set(id, (active.get(id) || 0) + 1)
      return () => { listeners.delete(fn); if (id) active.set(id, Math.max(0, active.get(id) - 1)) }
    },
    async revalidate() {
      await Promise.all([...active].filter(([id, count]) => count && !sessions.get(id)?.snapshot?.events.length).map(async ([id]) => {
        const session = sessions.get(id)
        if (!session || session.revalidating) return
        if (disposed) return
        session.revalidating = true
        const ticket = beginRead(id)
        try {
          const server = await read(id)
          if (!usableRead(ticket, server) || session.queue.snapshot().events.length) return
          session.server = server; cache(session); refresh(session)
        } catch { /* No edit is lost and offline read failures do not block scoring. */ }
        finally { if (!disposed) session.revalidating = false }
      }))
    },
    session: id => sessions.get(id),
    async open(id) {
      if (disposed) return
      if (sessions.has(id)) return sessions.get(id)
      let cached
      try { cached = JSON.parse(storage.getItem(prefix + id) || 'null') } catch {}
      if (cached?.id === id && cached.memberId && Array.isArray(cached.events)) {
        const session = install(id, cached)
        // Pending work starts directly from its durable authoritative baseline;
        // no GET is required before an idempotent retry.
        if (session.snapshot.events.length) { syncSession(session); return session }
      }
      const ticket = beginRead(id)
      try {
        const server = await read(id)
        if (!usableRead(ticket, server)) return disposed ? undefined : sessions.get(id)
        const session = sessions.get(id) || install(id, server)
        session.server = server; session.error = false; cache(session); refresh(session); syncSession(session)
        return session
      } catch (e) {
        if (!currentRead(ticket)) return disposed ? undefined : sessions.get(id)
        const session = sessions.get(id)
        if (session) {
          session.error = true
          if ([401,403,404].includes(e.status)) { session.model = null; try { storage.removeItem(prefix + id) } catch {} }
        }
        emit(); throw e
      }
    },
    restore() {
      if (disposed) return
      let size = 0
      try { size = storage.length } catch { return sync() }
      for (let i = 0; i < size; i++) {
        try {
          const key = storage.key(i)
          if (!key?.startsWith(prefix)) continue
          const server = JSON.parse(storage.getItem(key)), id = key.slice(prefix.length)
          const pending = JSON.parse(storage.getItem(racquetQueueKey({ clubCode, memberId: server.memberId, matchId: id })) || 'null')
          if (pending?.events?.length) install(id, server)
        } catch { /* Never overwrite unreadable local data. */ }
      }
      return sync()
    },
    submit(id, event) {
      if (disposed) throw new Error('store_disposed')
      const session = sessions.get(id)
      if (!session?.model?.permissions.canScore) throw new Error('scoring_forbidden')
      const existing = session.queue.snapshot().events.find(e => e.id === event.id)
      if (!existing) {
        if (event.expectedRevision !== session.model.revision) throw new Error('revision_conflict')
        const state = ['undo', 'replace_last_rally'].includes(event.operation.type)
          ? reconstructRacquet(session.server.initialState, [...session.model.events, event])
          : reduceRacquet(session.model.state, event.operation)
        session.candidate = { ...session.model, state, revision: event.expectedRevision + 1, events: [...session.model.events, event] }
      }
      const started = performance.now()
      enqueuedAt.set(event.id, performance.now())
      try { session.queue.enqueue(event) }
      catch (error) { session.candidate = null; throw error }
      if (session.queue.snapshot().storageError) throw Object.assign(new Error('queue_storage_failed'), { code: 'queue_storage_failed' })
      if (session.cacheError) {
        cache(session)
        if (session.cacheError) throw Object.assign(new Error('queue_storage_failed'), { code: 'queue_storage_failed' })
        refresh(session)
      }
      emitMemberClientTelemetry({ event: 'racquet_local_persist', requestId: event.id, totalMs: performance.now() - started })
      // A microtask gathers deliberate taps in the same task, without debouncing
      // or dropping any event. Existing in-flight batches retain FIFO ordering.
      queueMicrotask(() => syncSession(session))
      return event
    },
    async retry(id) {
      if (disposed) return
      const s = sessions.get(id)
      if (s.snapshot.blocked) {
        const ticket = beginRead(id)
        try {
          const server = await read(id)
          if (!usableRead(ticket, server)) return
          s.server = server; cache(s)
        } catch { if (!currentRead(ticket)) return; s.error = true }
        refresh(s); return
      }
      s.queue.retryStorage(); cache(s); refresh(s); return syncSession(s)
    },
    async discard(id) {
      if (disposed) throw new Error('store_disposed')
      const s = sessions.get(id), ticket = beginRead(id)
      const server = await read(id)
      // Explicit recovery must be reviewed again if anything changed while loading.
      if (!usableRead(ticket, server)) throw new Error('recovery_changed')
      s.server = server
      s.queue.discardAfterReview()
      s.error = false; cache(s); refresh(s)
    },
    sync,
    dispose() { disposed = true; controller.abort(); listeners.clear() }
  }
}
